<script lang="ts">
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import { Alert, Container, ListItem, Text } from '@Shelly/shelly-design-system/index';
	import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		getAlarmsWithDevice,
		getDashboardsWithDevice,
		getGroupsWithDevice,
		getScenesWithDevice,
		getThermostatsWithDevice,
	} from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/device_usage_helper';
	import { isDeviceSupportedInGroup } from 'src/helpers/group_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	export let device;

	const deviceChannels =
		Object.values($devices)
			.map((rawDevice) => {
				if (rawDevice.id !== device.id && rawDevice.id.includes(real_id(device.id)))
					return {
						_id: rawDevice.id,
						_meta: {
							name: rawDevice.name,
							image: rawDevice.image,
							backgroundColor: rawDevice.backgroundColor,
						},
					};
			})
			.filter((rawDevice) => rawDevice) || [];

	const collapsesArr = [
		{
			type: 'room',
			title: capFirstChar($format('process_room', { default: 'Room' })),
			data: $rooms[device.room_id],
			feedback_message: $format('feedback_message_room', {
				default: 'The device is hidden and currently not in any room.',
			}),
		},
		deviceChannels.length > 0 && {
			type: 'device',
			title: capFirstChar($format('label_channels', { default: 'Channels' })),
			data: deviceChannels,
			feedback_message: $format('feedback_message_channel', {
				default: 'The device has no other channels associated with it',
			}),
		},
		{
			type: 'dashboard',
			title: capFirstChar($format('tab_dashboards', { default: 'Dashboards' })),
			data: getDashboardsWithDevice(device.id),
			feedback_message: $format('feedback_message_dashboard', {
				default: 'The device is not currently in any dashboard.',
			}),
		},
		isDeviceSupportedInGroup(device) && {
			type: 'group',
			title: capFirstChar($format('tab_groups', { default: 'Groups' })),
			data: getGroupsWithDevice(device.id),
			feedback_message: $format('feedback_message_group', {
				default: 'The device is not used in any group.',
			}),
		},
		{
			type: 'scene',
			title: capFirstChar($format('tab_scenes', { default: 'Scenes' })),
			data: getScenesWithDevice(device.id),
			feedback_message: $format('feedback_message_scene', {
				default: 'The device is not used in any scene.',
			}),
		},
		{
			type: 'alarm',
			title: capFirstChar($format('tab_alarms', { default: 'Alarms' })),
			data: getAlarmsWithDevice(device.id),
			feedback_message: $format('feedback_message_alarm', {
				default: 'The device is not used in any alarm.',
			}),
		},
		{
			type: 'thermostat',
			title: capFirstChar($format('tab_thermostats', { default: 'Thermostats' })),
			data: getThermostatsWithDevice(device.id),
			feedback_message: $format('feedback_message_thermostat', {
				default: 'The device is not used in any thermostat.',
			}),
		},
	].filter(Boolean);

	const onRoomClick = (data) => {
		setSidePanelState({ type: 'room', id: data.id, open: false });
		return push(`/home/room/${data.id}/devices`);
	};

	const onCardClick = (item, type) => {
		if (type === 'dashboard') {
			setSidePanelState({ type: 'dashboard', id: item._id, open: false });
			return push(`/dash/${item._id}`);
		}
		setSidePanelState({ type, id: item._id, open: true });
	};

	const progress: any = {
		percent: 100,
		color: 'unknown',
		showPercentBar: true,
	};

	const getSectionCount = (index, data) => {
		if (index === 0) return data ? 1 : 0;
		return data?.length || 0;
	};

	const getSectionSpacingClass = (isLastSection) => {
		if (isLastSection) return 'mb-0';
		if ($showNewLayout) return 'mb-l';
		return 'mb-s';
	};

	const getThermostatIcon = (type, thermostatType) => {
		if (type !== 'thermostat') return '';
		if (thermostatType === 'cool') return 'fad fa-snowflake';
		return 'fad fa-sun';
	};

	const getListItemClass = () => {
		if ($showNewLayout) return 'bg-color-1';
		return '';
	};

	const getListItemProgress = () => {
		if ($showNewLayout) return { ...progress, showPercentBar: false };
		return progress;
	};
</script>

{#each collapsesArr as { type, title, data, feedback_message }, index}
	{@const sectionCount = getSectionCount(index, data)}
	{@const isEmptySection = sectionCount === 0}
	{@const isLastSection = index === collapsesArr.length - 1}
	<div class={getSectionSpacingClass(isLastSection)}>
		<Container padding={$showNewLayout ? '0' : 'xs'}>
			{#if isEmptySection}
				<Text
					class={`mb-s ${!$showNewLayout ? 'text-bold' : ''}`}
					styleType={$showNewLayout ? 'body' : undefined}
				>
					{`${title}: `}
					{#if $showNewLayout || index > 0}
						<span class="text-primary text-bold">{sectionCount}</span>{/if}
				</Text>
				{#if $showNewLayout}
					<Text styleType="body-small" class="text-color-muted">{feedback_message}</Text>
				{:else}
					<Alert type="info" heading={feedback_message} />
				{/if}
			{:else}
				<Text
					class={`mb-s ${!$showNewLayout ? 'text-bold' : ''}`}
					styleType={$showNewLayout ? 'body' : undefined}
				>
					{`${title}: `}
					{#if $showNewLayout || index > 0}
						<span class="text-primary text-bold">{sectionCount}</span>
					{/if}
				</Text>
				{#if index === 0}
					<div class:dnd-placeholder={data.isDndShadowItem}>
						<ListItem
							class={getListItemClass()}
							title={data.name}
							backgroundColor={$showNewLayout ? undefined : data.backgroundColor}
							image={data.image && load_image_from(data.image)}
							on:click={() => onRoomClick(data)}
							viewType="bar"
							progress={getListItemProgress()}
							actionBtn
						/>
					</div>
				{:else}
					{#each data as item, itemIndex}
						<div
							class={itemIndex === data.length - 1 ? 'mb-0' : 'mb-s'}
							class:dnd-placeholder={item.isDndShadowItem}
						>
							<ListItem
								class={getListItemClass()}
								title={item._meta.name}
								icon={getThermostatIcon(type, item._meta.type)}
								backgroundColor={$showNewLayout ? undefined : item._meta.backgroundColor}
								image={item._meta.image && load_image_from(item._meta.image)}
								on:click={() => onCardClick(item, type)}
								viewType="bar"
								progress={getListItemProgress()}
								actionBtn
							/>
						</div>
					{/each}
				{/if}
			{/if}
		</Container>
	</div>
{/each}
